/**
 * StrainDetailPage Component
 *
 * Detail/edit page for individual mushroom strains.
 * Three-column table layout with row-spanning sections and inline editing.
 *
 * US-PE-001: Strains Management Page
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useStrain, useStrainMutations } from '@/hooks/useStrains'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'
import { ArrowLeft, AlertCircle, Check, Loader2 } from 'lucide-react'

/**
 * Debounce hook for auto-save
 */
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null)

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args)
    }, delay)
  }, [callback, delay])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return debouncedCallback
}

/**
 * Editable text input cell
 */
function TextInput({ value, onChange, placeholder, className = '', mono = false }) {
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`border-0 bg-transparent focus:bg-white focus:ring-1 h-9 ${mono ? 'font-mono' : ''} ${className}`}
    />
  )
}

/**
 * Editable number input
 */
function NumberInput({ value, onChange, placeholder, min, max, step = 1 }) {
  return (
    <Input
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      className="border-0 bg-transparent focus:bg-white focus:ring-1 h-9 w-24"
    />
  )
}

/**
 * Range display (min - max with unit)
 */
function RangeInput({ minValue, maxValue, onMinChange, onMaxChange, unit }) {
  return (
    <div className="flex items-center">
      <Input
        type="number"
        value={minValue ?? ''}
        onChange={(e) => onMinChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="Min"
        className="border-0 bg-transparent focus:bg-white focus:ring-1 h-9 w-16 text-right"
      />
      <span className="text-muted-foreground w-6 text-center">-</span>
      <Input
        type="number"
        value={maxValue ?? ''}
        onChange={(e) => onMaxChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="Max"
        className="border-0 bg-transparent focus:bg-white focus:ring-1 h-9 w-16 text-right"
      />
      <span className="text-muted-foreground w-12 text-left ml-2">{unit}</span>
    </div>
  )
}

/**
 * Value with unit display
 */
function ValueWithUnit({ value, onChange, placeholder, min, max, step = 1, unit }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        className="border-0 bg-transparent focus:bg-white focus:ring-1 h-9 w-16 text-right"
      />
      <span className="text-muted-foreground">{unit}</span>
    </div>
  )
}

// Default values for new strain
const defaultStrainData = {
  name: '',
  code: '',
  common_name: '',
  binomial_name: '',
  category: 'gourmet',
  origin: 'commercial',
  active: true,
  inoculation_rate_percent: null,
  substrate_types: [],
  incubation_room_temp_min: null,
  incubation_room_temp_max: null,
  incubation_substrate_temp_min: null,
  incubation_substrate_temp_max: null,
  typical_incubation_days_min: null,
  typical_incubation_days_max: null,
  primordia_temp_min: null,
  primordia_temp_max: null,
  primordia_humidity_min: null,
  primordia_humidity_max: null,
  fruiting_temp_min: null,
  fruiting_temp_max: null,
  fruiting_humidity: null,
  fruiting_co2_max_ppm: null,
  fruiting_light_lux_min: null,
  fruiting_light_lux_max: null,
  typical_fruiting_days_min: null,
  typical_fruiting_days_max: null,
  expected_flush_count: null,
  typical_flush_interval_days_min: null,
  typical_flush_interval_days_max: null,
  flush_yield_distribution: [],
  expected_yield_percent: null,
  typical_lc_days_min: null,
  typical_lc_days_max: null,
  typical_spawn_days_min: null,
  typical_spawn_days_max: null,
  notes: ''
}

export default function StrainDetailPage({ user, onLogout }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'
  const { strain, loading, error, refetch } = useStrain(isNew ? null : id)
  const { updateStrain, createStrain, updating } = useStrainMutations()

  // Form state
  const [formData, setFormData] = useState(null)
  const [saveStatus, setSaveStatus] = useState(isNew ? 'unsaved' : 'saved') // 'saved' | 'saving' | 'error' | 'unsaved'
  const [strainId, setStrainId] = useState(isNew ? null : id)

  // Product reference_code (lives on products table, keyed by strain name)
  const [productRefCode, setProductRefCode] = useState('')
  const [productId, setProductId] = useState(null)

  // Process generation modal state
  const [showProcessModal, setShowProcessModal] = useState(false)
  const [newStrainId, setNewStrainId] = useState(null)
  const [generatingProcess, setGeneratingProcess] = useState(false)

  // Initialize form data when strain loads or for new strain
  useEffect(() => {
    if (isNew) {
      setFormData({ ...defaultStrainData })
    } else if (strain) {
      setFormData({ ...strain })
    }
  }, [strain, isNew])

  // Load matching product's reference_code (products table is separate from strains)
  useEffect(() => {
    if (!formData?.name) return
    supabase
      .from('products')
      .select('id, reference_code')
      .eq('strain', formData.name)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setProductId(data.id)
          setProductRefCode(data.reference_code || '')
        }
      })
  }, [formData?.name])

  // Auto-save function (for existing strains only)
  const saveChanges = useCallback(async (data) => {
    if (!data || !strainId) return

    setSaveStatus('saving')

    const { error } = await updateStrain(strainId, data)

    if (error) {
      setSaveStatus('error')
      toast.error('Failed to save')
    } else {
      setSaveStatus('saved')
    }
  }, [strainId, updateStrain])

  // Debounced save (500ms after last change)
  const debouncedSave = useDebounce(saveChanges, 500)

  // Save product reference_code to products table
  const saveProductRefCode = useCallback(async (code) => {
    if (!productId) return
    const { error } = await supabase
      .from('products')
      .update({ reference_code: code || null })
      .eq('id', productId)
    if (error) {
      toast.error('Failed to save item ref code')
    }
  }, [productId])

  const debouncedSaveProductRef = useDebounce(saveProductRefCode, 500)

  const handleProductRefChange = (value) => {
    const code = value.toUpperCase().slice(0, 6)
    setProductRefCode(code)
    debouncedSaveProductRef(code)
  }

  // Update field and trigger auto-save (only for existing strains)
  const updateField = (field, value) => {
    const newData = { ...formData, [field]: value }
    setFormData(newData)

    if (strainId) {
      // Existing strain - auto-save
      setSaveStatus('saving')
      debouncedSave(newData)
    } else {
      // New strain - just mark as unsaved
      setSaveStatus('unsaved')
    }
  }

  // Create new strain
  const handleCreate = async () => {
    if (!formData?.name || !formData?.code) {
      toast.error('Name and Code are required')
      return
    }

    setSaveStatus('saving')
    const { data, error } = await createStrain(formData)

    if (error) {
      setSaveStatus('error')
      toast.error('Failed to create strain')
    } else {
      toast.success('Strain created')
      // Store the new strain ID and show the process generation modal
      setNewStrainId(data.id)
      setShowProcessModal(true)
    }
  }

  // Generate production process from strain
  const handleGenerateProcess = async () => {
    if (!newStrainId) return

    setGeneratingProcess(true)
    try {
      const { data, error } = await supabase.rpc('generate_process_from_strain', {
        p_strain_id: newStrainId
      })

      if (error) throw error

      toast.success('Production process created successfully')
      setShowProcessModal(false)
      // Navigate to the new strain's detail page
      navigate(`/production/strains/${newStrainId}`, { replace: true })
    } catch (err) {
      console.error('Failed to generate process:', err)
      toast.error('Failed to create production process')
    } finally {
      setGeneratingProcess(false)
    }
  }

  // Skip process generation and just navigate
  const handleSkipProcess = () => {
    setShowProcessModal(false)
    if (newStrainId) {
      navigate(`/production/strains/${newStrainId}`, { replace: true })
    }
  }

  const handleBack = () => {
    navigate('/production/strains')
  }

  if (!isNew && loading) {
    return (
      <PageLayout title="Strain Details" user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="ml-3 text-muted-foreground">Loading strain...</p>
        </div>
      </PageLayout>
    )
  }

  if (!isNew && (error || !strain)) {
    return (
      <PageLayout title="Strain Details" user={user} onLogout={onLogout}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Strain not found'}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Strains
        </Button>
      </PageLayout>
    )
  }

  // Table cell styles
  const cellBase = "border border-border px-4 py-3 align-top"
  const sectionCell = `${cellBase} text-sm`
  const labelCell = `${cellBase} text-sm`
  const valueCell = `${cellBase}`

  return (
    <PageLayout
      title={
        isNew ? (
          'New Strain'
        ) : (
          <div className="flex items-center gap-3">
            <span>{formData?.common_name || formData?.name || strain?.name}</span>
            {formData?.code && (
              <Badge variant="secondary" className="font-mono text-xs">
                {formData?.code || strain?.code}
              </Badge>
            )}
          </div>
        )
      }
      subtitle={isNew ? 'Create a new mushroom strain' : (formData?.binomial_name || strain?.scientific_name)}
      user={user}
      onLogout={onLogout}
      headerActions={
        <div className="flex items-center gap-4">
          {isNew ? (
            <>
              {/* Create button for new strains */}
              <Button onClick={handleCreate} disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Strain'
                )}
              </Button>
              <Button variant="outline" onClick={handleBack}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData?.active ?? true}
                  onCheckedChange={(v) => updateField('active', v)}
                  style={{ backgroundColor: formData?.active ? '#10b981' : '#d1d5db' }}
                />
                <span className={`text-sm ${formData?.active ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                  {formData?.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {/* Save status indicator */}
              <div className="flex items-center gap-2 text-sm">
                {saveStatus === 'saving' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-emerald-600">Saved</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-red-600">Error saving</span>
                  </>
                )}
              </div>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="h-full overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <table className="w-full border-collapse">
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <tbody>
              {/* Basic Info - Compact 4-column layout for paired fields */}
              <tr>
                <td className={labelCell}>Binomial</td>
                <td colSpan={3} className={valueCell}>
                  <TextInput
                    value={formData?.binomial_name}
                    onChange={(v) => updateField('binomial_name', v)}
                    placeholder="e.g., Pleurotus ostreatus"
                    className="italic"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>Name</td>
                <td className={valueCell}>
                  <TextInput
                    value={formData?.name}
                    onChange={(v) => updateField('name', v)}
                    placeholder="e.g., Grey Oyster"
                  />
                </td>
                <td className={labelCell}>Code</td>
                <td className={valueCell}>
                  <TextInput
                    value={formData?.code}
                    onChange={(v) => updateField('code', v.toUpperCase())}
                    placeholder="GO"
                    mono
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>Common name</td>
                <td className={valueCell}>
                  <TextInput
                    value={formData?.common_name}
                    onChange={(v) => updateField('common_name', v)}
                    placeholder="e.g., Grey Oyster"
                  />
                </td>
                <td className={labelCell}>Category</td>
                <td className={valueCell}>
                  <Select
                    value={formData?.category || 'gourmet'}
                    onValueChange={(v) => updateField('category', v)}
                  >
                    <SelectTrigger className="w-full h-9 border-0 bg-transparent focus:ring-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gourmet">Gourmet</SelectItem>
                      <SelectItem value="medicinal">Medicinal</SelectItem>
                      <SelectItem value="specialty">Specialty</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
              <tr>
                <td className={labelCell}>Origin</td>
                <td className={valueCell}>
                  <Select
                    value={formData?.origin || 'commercial'}
                    onValueChange={(v) => updateField('origin', v)}
                  >
                    <SelectTrigger className="w-full h-9 border-0 bg-transparent focus:ring-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="native">Native (foraged/locally isolated)</SelectItem>
                      <SelectItem value="commercial">Commercial (purchased)</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className={labelCell}>Item Ref</td>
                <td className={valueCell}>
                  {productId ? (
                    <Input
                      value={productRefCode}
                      onChange={(e) => handleProductRefChange(e.target.value)}
                      placeholder={formData?.name?.substring(0, 3).toUpperCase() || 'AUTO'}
                      maxLength={6}
                      className="border-0 bg-transparent focus:bg-white focus:ring-1 h-9 font-mono uppercase w-20"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">No product linked</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className={labelCell}>Inoculation rate</td>
                <td className={valueCell}>
                  <ValueWithUnit
                    value={formData?.inoculation_rate_percent}
                    onChange={(v) => updateField('inoculation_rate_percent', v)}
                    placeholder="2.0"
                    min={0}
                    max={100}
                    step={0.5}
                    unit="% spawn"
                  />
                </td>
                <td className={labelCell}>Substrate</td>
                <td className={valueCell}>
                  <TextInput
                    value={formData?.substrate_types?.join(', ')}
                    onChange={(v) => updateField('substrate_types', v.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="hardwood, straw"
                  />
                </td>
              </tr>

              {/* Incubation - 4-column with row span */}
              <tr>
                <td className={sectionCell} rowSpan={3}>Incubation</td>
                <td className={labelCell}>room temperature:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.incubation_room_temp_min}
                    maxValue={formData?.incubation_room_temp_max}
                    onMinChange={(v) => updateField('incubation_room_temp_min', v)}
                    onMaxChange={(v) => updateField('incubation_room_temp_max', v)}
                    unit="°C"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>substrate temperature:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.incubation_substrate_temp_min}
                    maxValue={formData?.incubation_substrate_temp_max}
                    onMinChange={(v) => updateField('incubation_substrate_temp_min', v)}
                    onMaxChange={(v) => updateField('incubation_substrate_temp_max', v)}
                    unit="°C"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>duration:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.typical_incubation_days_min}
                    maxValue={formData?.typical_incubation_days_max}
                    onMinChange={(v) => updateField('typical_incubation_days_min', v)}
                    onMaxChange={(v) => updateField('typical_incubation_days_max', v)}
                    unit="days"
                  />
                </td>
              </tr>

              {/* Primordia Induction */}
              <tr>
                <td className={sectionCell} rowSpan={2}>Primordia induction</td>
                <td className={labelCell}>temperature:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.primordia_temp_min}
                    maxValue={formData?.primordia_temp_max}
                    onMinChange={(v) => updateField('primordia_temp_min', v)}
                    onMaxChange={(v) => updateField('primordia_temp_max', v)}
                    unit="°C"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>relative humidity:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.primordia_humidity_min}
                    maxValue={formData?.primordia_humidity_max}
                    onMinChange={(v) => updateField('primordia_humidity_min', v)}
                    onMaxChange={(v) => updateField('primordia_humidity_max', v)}
                    unit="%"
                  />
                </td>
              </tr>

              {/* Fruiting Conditions */}
              <tr>
                <td className={sectionCell} rowSpan={5}>Fruiting conditions</td>
                <td className={labelCell}>room temperature:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.fruiting_temp_min}
                    maxValue={formData?.fruiting_temp_max}
                    onMinChange={(v) => updateField('fruiting_temp_min', v)}
                    onMaxChange={(v) => updateField('fruiting_temp_max', v)}
                    unit="°C"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>relative humidity:</td>
                <td colSpan={2} className={valueCell}>
                  <ValueWithUnit
                    value={formData?.fruiting_humidity}
                    onChange={(v) => updateField('fruiting_humidity', v)}
                    placeholder="85"
                    min={0}
                    max={100}
                    unit="%"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>max CO2:</td>
                <td colSpan={2} className={valueCell}>
                  <ValueWithUnit
                    value={formData?.fruiting_co2_max_ppm}
                    onChange={(v) => updateField('fruiting_co2_max_ppm', v)}
                    placeholder="800"
                    min={0}
                    max={5000}
                    step={100}
                    unit="ppm"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>light:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.fruiting_light_lux_min}
                    maxValue={formData?.fruiting_light_lux_max}
                    onMinChange={(v) => updateField('fruiting_light_lux_min', v)}
                    onMaxChange={(v) => updateField('fruiting_light_lux_max', v)}
                    unit="lux"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>duration:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.typical_fruiting_days_min}
                    maxValue={formData?.typical_fruiting_days_max}
                    onMinChange={(v) => updateField('typical_fruiting_days_min', v)}
                    onMaxChange={(v) => updateField('typical_fruiting_days_max', v)}
                    unit="days"
                  />
                </td>
              </tr>

              {/* Flush & Yield */}
              <tr>
                <td className={sectionCell} rowSpan={4}>Flush &amp; yield</td>
                <td className={labelCell}>expected flush count:</td>
                <td colSpan={2} className={valueCell}>
                  <ValueWithUnit
                    value={formData?.expected_flush_count}
                    onChange={(v) => updateField('expected_flush_count', v)}
                    placeholder="3"
                    min={1}
                    max={10}
                    unit="flushes"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>flush interval:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.typical_flush_interval_days_min}
                    maxValue={formData?.typical_flush_interval_days_max}
                    onMinChange={(v) => updateField('typical_flush_interval_days_min', v)}
                    onMaxChange={(v) => updateField('typical_flush_interval_days_max', v)}
                    unit="days"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>yield distribution:</td>
                <td colSpan={2} className={valueCell}>
                  <TextInput
                    value={formData?.flush_yield_distribution?.join(', ')}
                    onChange={(v) => updateField('flush_yield_distribution', v.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)))}
                    placeholder="e.g., 60, 30, 10"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>expected yield:</td>
                <td colSpan={2} className={valueCell}>
                  <ValueWithUnit
                    value={formData?.expected_yield_percent}
                    onChange={(v) => updateField('expected_yield_percent', v)}
                    placeholder="100"
                    min={0}
                    max={300}
                    step={5}
                    unit="%"
                  />
                </td>
              </tr>

              {/* Production Timing */}
              <tr>
                <td className={sectionCell} rowSpan={2}>Production timing</td>
                <td className={labelCell}>LC colonisation:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.typical_lc_days_min}
                    maxValue={formData?.typical_lc_days_max}
                    onMinChange={(v) => updateField('typical_lc_days_min', v)}
                    onMaxChange={(v) => updateField('typical_lc_days_max', v)}
                    unit="days"
                  />
                </td>
              </tr>
              <tr>
                <td className={labelCell}>spawn colonisation:</td>
                <td colSpan={2} className={valueCell}>
                  <RangeInput
                    minValue={formData?.typical_spawn_days_min}
                    maxValue={formData?.typical_spawn_days_max}
                    onMinChange={(v) => updateField('typical_spawn_days_min', v)}
                    onMaxChange={(v) => updateField('typical_spawn_days_max', v)}
                    unit="days"
                  />
                </td>
              </tr>

              {/* Notes */}
              <tr>
                <td className={labelCell}>Notes</td>
                <td colSpan={3} className={valueCell}>
                  <TextInput
                    value={formData?.notes}
                    onChange={(v) => updateField('notes', v)}
                    placeholder="Additional notes..."
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Process Generation Confirmation Modal */}
      <Dialog open={showProcessModal} onOpenChange={setShowProcessModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create production process?</DialogTitle>
            <DialogDescription>
              Would you like to generate a production process from this strain&apos;s cultivation parameters?
              This will create phases, events, and tasks based on the timing information you&apos;ve entered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleSkipProcess}
              disabled={generatingProcess}
            >
              No
            </Button>
            <Button
              onClick={handleGenerateProcess}
              disabled={generatingProcess}
            >
              {generatingProcess ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Yes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}
